<template>
  <div class="commbook">
    <div class="commbook-search">
      <div class="commbook-search-box">
        <input type="text" v-model="keywords" placeholder="Search" @keyup.enter="handleSearch" />
        <i class="iconfont iconControlsearchbar2-24px" @click="handleSearch"></i>
      </div>
    </div>
    <div class="commbook-result" v-loading="loading">
      <div class="commbook-list" v-if="list.length > 0">
        <div class="commbook-item" v-for="item in list" :key="item.id" @click="toPreview(item)">
          <div class="commbook-item-left">
            <div class="commbook-title">{{ item.title }}</div>
            <div class="commbook-topic">{{ item.topic }}</div>
          </div>
          <div class="commbook-date">{{ formatTime(item.modifyTime) }}</div>
        </div>
      </div>
      <div class="empty-box" slot="empty" v-else>
        <img :src="require('@/assets/images/moments/empty_photo.png')" />
        <p>No commbook yet.</p>
      </div>
      <!-- ======= -->
      <!-- 分页组件 -->
      <!-- ======= -->
      <edoovo-pagination
        slot="pagination"
        @size-change="_pageSizeChange"
        @current-change="_pageChange"
        :current-page="pageNum"
        :page-size="size"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
import { Ajax } from '@/common'
import { readState, listState } from '@/common/nav-state'
import moments from 'moment'

// URL 读出的 current/size 只接受正整数；垃圾值回退默认值，保证内存值与请求体是 number
function toPageInt(value, fallback) {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : fallback
}

export default {
  name: 'CommBookView',
  props: {
    studentInfo: {
      type: Object
    }
  },
  data() {
    return {
      // Define any data properties needed for the component
      list: [],
      loading: false,
      pageNum: 1,
      pages: 0,
      total: 0,
      size: 20,
      keywords: ''
    }
  },
  computed: {
    studentId() {
      const s = readState(this.$route, ['id', 'type'])
      return s ? s.id : ''
    }
  },
  watch: {
    '$route.query.id'() {
      // 同组件复用（A→B 后退）时重新读参取数；列表态变化归口 _resyncFromRoute 去重，一次导航只取数一次
      this._resyncFromRoute()
    },
    '$route.query': {
      deep: true,
      handler() {
        // 浏览器前进/后退复用组件：URL 与本页最近导航不一致时按 URL 重新播种并重查
        this._resyncFromRoute()
      }
    }
  },
  created() {
    if (!readState(this.$route, ['id', 'type'])) {
      if (this.$message) this.$message.error('Missing student')
      this.$router.replace('/students')
      return
    }
    // 列表态以 URL 为准；缺失的默认键只回填一次（router.replace）。
    // 身份键（id/type）不在列表态键内，push 合并语义原样保留。
    const state = listState.defaultsFrom(this.$router, this.$route, {
      current: '1',
      size: '20',
      keywords: ''
    })
    this._seedFromQuery(state)
    this._navState = this._normalizeNavState(state)
    this._navIdentity = this.studentId
    this.getList()
  },
  methods: {
    // Define any methods needed for the component
    formatTime(time) {
      if (!time) return '-'
      return moments(time, 'YYYY-MM-DD HH:mm:ss').local().format('DD/MM/YYYY, H:mm A')
    },
    _pageSizeChange(val) {
      this.size = val
      this._pushList({ size: val })
      this.getList()
    },
    _pageChange(val) {
      this.pageNum = val
      this._pushList({ current: val })
      this.getList()
    },
    // 列表 URL 状态的可比对键（id/type 是身份键，不参与）
    _stateKeys() {
      return ['keywords', 'current', 'size']
    },
    // 缺失/null 键折叠为 ''，保证 push 产生的“删键”与读取侧可比
    _normalizeNavState(map) {
      const out = {}
      this._stateKeys().forEach((key) => {
        const v = map[key]
        out[key] = v === undefined || v === null ? '' : String(v)
      })
      return out
    },
    _sameNavState(a, b) {
      return this._stateKeys().every((key) => a[key] === b[key])
    },
    // 自身导航统一入口：push 后记录本页最近一次 URL 状态，供 $route 监听识别自身导航
    _pushList(patch) {
      const query = listState.push(this.$router, this.$route, patch)
      this._navState = this._normalizeNavState(query)
    },
    // 以 URL 状态重建列表内存状态（created 与前进/后退复用共用）
    // 改写披露：身份切换不再强制回第 1 页——URL 里的 current 即真相（新导航无键=1，后退回原页码）
    _seedFromQuery(state) {
      this.keywords = String(state.keywords || '')
      this.pageNum = toPageInt(state.current, 1)
      this.size = toPageInt(state.size, 20)
    },
    // 身份（$route.query.id）与列表态共用同一重同步入口：两条 watcher 先后触发也只落地一次取数
    _resyncFromRoute() {
      const state = listState.read(this.$route, this._stateKeys(), {
        current: '1',
        size: '20',
        keywords: ''
      })
      const navState = this._normalizeNavState(state)
      const identity = this.studentId
      if (this._sameNavState(navState, this._navState) && identity === this._navIdentity) return
      this._navState = navState
      this._navIdentity = identity
      this._seedFromQuery(state)
      this.getList()
    },
    getList() {
      // Implement the logic to fetch the list of communication books
      // This could involve making an API call to retrieve data
      this.loading = true
      let params = {
        keywords: this.keywords,
        studentId: this.studentId,
        current: this.pageNum,
        size: this.size // Adjust the size as needed
      }
      return Ajax.post('/commbook/title/getListV1', params).then((res) => {
        this.loading = false
        let { records } = res.data
        this.list = records
        this.total = res.data.total
        this.pages = res.data.pages
      })
    },
    handleSearch() {
      this.pageNum = 1
      this._pushList({ keywords: this.keywords, current: 1 })
      this.getList()
    },
    toPreview(item) {
      sessionStorage.setItem('commbookPreview', JSON.stringify(item))
      let path = this.$router.resolve({
        name: 'studentsCommbookPreview',
        query: {
          firstName: this.studentInfo.firstName,
          lastName: this.studentInfo.lastName
        }
      })
      window.open(path.href, '_blank')
    }
  }
}
</script>

<style lang="less" scoped>
.commbook {
  height: calc(100% - 97px);

  &-search {
    margin-top: 20px;
    margin-bottom: 20px;

    .commbook-search-box {
      width: 280px;
      height: 40px;
      border-radius: 2px;
      background-color: #f5f8fa;
      border: 1px solid #cbd6e2;
      display: flex;
      align-items: center;

      input {
        flex: 1;
        height: 38px;
        outline: none;
        border: none;
        padding: 0 8px;
        font-family: OpenSans;
        font-size: 14px;
        color: #202124;
        background-color: #f5f8fa;
      }

      i {
        font-size: 24px;
        color: #0091ae;
        cursor: pointer;
      }
    }
  }

  &-result {
    height: calc(100% - 80px);
    font-family: Roboto;
    font-size: 14px;
    color: #202124;
    width: 100%;

    .commbook-list {
      width: 100%;
      height: calc(100% - 40px);
      overflow: auto;
      border-top: 1px solid #e0e0e0;
      margin-bottom: 16px;

      .commbook-item {
        display: flex;
        justify-content: space-between;
        width: 100%;
        height: 72px;
        align-items: center;
        padding: 0 20px;
        border-bottom: 1px solid #e0e0e0;
        cursor: pointer;
        background-color: #fff;

        &-left {
          display: flex;
          align-items: center;

          .commbook-title {
            font-family: Roboto;
            font-size: 14px;
            color: #202124;
          }

          .commbook-topic {
            flex: none;
            font-family: Roboto;
            font-size: 12px;
            color: #1a73e8;
            height: 18px;
            display: flex;
            align-items: center;
            border-radius: 10px;
            background-color: #e8f0fe;
            padding: 0 8px;
            margin-left: 4px;
          }
        }

        .commbook-date {
          font-family: Roboto;
          font-size: 14px;
          color: rgba(0, 0, 0, 0.5);
        }

        &:hover {
          background-color: #eeeeee;
        }
      }
    }
  }
}
.empty-box {
  position: fixed;
  left: 50%;
  top: 50%;
  margin: -125px 0 0 -107px;
  width: 214px;
  height: 250px;
  text-align: center;
  img {
    width: 100%;
    display: block;
  }

  p {
    margin-top: 20px;
    font-size: 20px;
    font-family: Roboto;
    color: #606367;
  }
}
</style>
